// Domain-status semantic palette. Six buckets — success / warning / error /
// info / neutral / muted — collapse the 5+ domain enums into a stable visual
// vocabulary so the operator gets the same visual signal across pages.

export type Domain =
  | 'account'
  | 'warehouse'
  | 'campaign'
  | 'campaign-item'
  | 'trade'
  | 'recipient'
  | 'proxy';

export type Bucket = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'muted';

const ACCOUNT_BUCKETS = {
  warming_up: 'info',
  available: 'success',
  cooldown: 'warning',
  disabled: 'muted',
  banned: 'error',
} as const satisfies Record<string, Bucket>;

const WAREHOUSE_BUCKETS = {
  active: 'success',
  paused: 'warning',
  disabled: 'muted',
} as const satisfies Record<string, Bucket>;

const CAMPAIGN_BUCKETS = {
  draft: 'neutral',
  running: 'success',
  paused: 'warning',
  cancelled: 'error',
  completed: 'info',
} as const satisfies Record<string, Bucket>;

const CAMPAIGN_ITEM_BUCKETS = {
  pending: 'neutral',
  snapshotted: 'info',
  enqueued: 'info',
  consumed: 'success',
  skipped: 'muted',
} as const satisfies Record<string, Bucket>;

const TRADE_BUCKETS = {
  queued: 'neutral',
  sending: 'info',
  sent: 'info',
  accepted: 'success',
  cancelled: 'muted',
  expired: 'muted',
  declined: 'error',
  failed: 'error',
} as const satisfies Record<string, Bucket>;

const RECIPIENT_BUCKETS = {
  pending: 'neutral',
  enqueued: 'info',
  sent: 'info',
  completed: 'success',
  quarantined: 'error',
  resolved: 'muted',
} as const satisfies Record<string, Bucket>;

// Plan 08-03 — proxy health surfaced on /accounts. `null` proxyHealth
// (account without proxy) lands on the muted fallback via getBucket().
const PROXY_BUCKETS = {
  ok: 'success',
  soft: 'warning',
  down: 'error',
} as const satisfies Record<string, Bucket>;

const STATUS_BUCKETS: Record<Domain, Record<string, Bucket>> = {
  account: ACCOUNT_BUCKETS,
  warehouse: WAREHOUSE_BUCKETS,
  campaign: CAMPAIGN_BUCKETS,
  'campaign-item': CAMPAIGN_ITEM_BUCKETS,
  trade: TRADE_BUCKETS,
  recipient: RECIPIENT_BUCKETS,
  proxy: PROXY_BUCKETS,
};

export const getBucket = (domain: Domain, state: string): Bucket =>
  STATUS_BUCKETS[domain][state] ?? 'muted';

export const BUCKET_TO_TAILWIND: Record<Bucket, string> = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500',
  info: 'bg-sky-500',
  neutral: 'bg-slate-400',
  muted: 'bg-slate-300',
};
